import React, { useState } from "react"
import { Box, Typography, TextField, Button, Link, CircularProgress,
  FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, FormHelperText
} from "@mui/material"
import { VisibilityOff, Visibility } from "@mui/icons-material"

import { post, get } from "../utils/http"
import { isPrivate } from "../utils/config"
import logo from '../assets/pathomiq-logo.png'

interface FormValues {
  [key: string]: string,
  first_name: string,
  last_name: string,
  email: string,
  password: string,
  confirm_password: string,
  organization: string,
}

const SignUp = () => {
  const [signupForm, setSignupForm] = useState<FormValues>({
    title: 'Dr.',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    organization: ''
  })
  const [signupFormError, setSignupFormError] = useState<FormValues>({
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    organization: ''
  })
  const [isLoading, setIsloading] = useState<boolean>(false)
  const [formResMsg, setFormResMsg] = useState<string>('')
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
  const [regWelcome, setRegWelcome] = useState<boolean>(false)

  const minPassLength: number = 8

  const checkEmailExist = async () => {
    try {
      await get(`Users/email/${signupForm.email}`)
      return true
    } catch (err) {
      return false
    }
  }

  const validateForm = async () => {
    let valid = true

    // Title check
    if (!signupForm.title || !signupForm.title.length) {
      setSignupFormError(prevForm => ({...prevForm, title: 'Title is required'}))
      valid = false
    } else setSignupFormError(prevForm => ({...prevForm, title: ''}))
    // first_name check
    if (!signupForm.first_name || !signupForm.first_name.length) {
      setSignupFormError(prevForm => ({...prevForm, first_name: 'First name is required'}))
      valid = false
    } else setSignupFormError(prevForm => ({...prevForm, first_name: ''}))
    // last_name check
    if (!signupForm.last_name || !signupForm.last_name.length) {
      setSignupFormError(prevForm => ({...prevForm, last_name: 'Last name is required'}))
      valid = false
    } else setSignupFormError(prevForm => ({...prevForm, last_name: ''}))
    // email check
    if (!signupForm.email) {
      setSignupFormError(prevForm => ({...prevForm, email: 'Email is required'}))
      valid = false
    } else {
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i
      if (!emailRegex.test(signupForm.email)) {
        setSignupFormError(prevForm => ({...prevForm, email: 'Invalid email address'}))
        valid = false
      } else {
        const emailExists = await checkEmailExist()
        if (emailExists) {
          setSignupFormError(prevForm => ({...prevForm, email: 'Email already registered'}))
          valid = false
        } else setSignupFormError(prevForm => ({...prevForm, email: ''}))
      }
    }
    // password check
    if (!signupForm.password) {
      setSignupFormError(prevForm => ({...prevForm, password: 'Password is required'}))
      valid = false
    } else if (signupForm.password.length < minPassLength) {
      setSignupFormError(prevForm => ({...prevForm, password: 'Your password must be at least 8 characters'}))
      valid = false
    } else setSignupFormError(prevForm => ({...prevForm, password: ''}))
    // confirm password
    if (!signupForm.confirm_password) {
      setSignupFormError(prevForm => ({...prevForm, confirm_password: 'Confirm the password'}))
      valid = false
    } else if (signupForm.confirm_password !== signupForm.password) {
      setSignupFormError(prevForm => ({...prevForm, confirm_password: 'Your password must match'}))
      valid = false
    } else setSignupFormError(prevForm => ({...prevForm, confirm_password: ''}))
    // organization check
    if (!signupForm.organization) {
      setSignupFormError(prevForm => ({...prevForm, organization: 'Organization is required'}))
      valid = false
    } else setSignupFormError(prevForm => ({...prevForm, organization: ''}))

    return valid
  }

  const handleChange = (e: any) => {
    setFormResMsg('')
    setSignupForm(prevForm => ({...prevForm, [e.target.name]: e.target.value}))
    setSignupFormError(prevForm => ({...prevForm, [e.target.name]: ''}))
  }

  const handleClickShowPassword = () => setShowPassword(show => !show)
  const handleMouseDownPassword = (e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()
  const handleMouseUpPassword = (e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()

  const handleClickShowConfirmPassword = () => setShowConfirmPassword(show => !show)
  const handleMouseDownConfirmPassword = (e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()
  const handleMouseUpConfirmPassword = (e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()

  const signupHandler = async () => {
    setIsloading(true)
    try {
      const formValid = await validateForm()
      if (!formValid) throw Error('Form invalid')
      await post("Users/registration", signupForm)
      setIsloading(false)
      setRegWelcome(true)
    } catch (err) {
      console.log(err)
      setIsloading(false)
      setFormResMsg('Request failed')
    }
  }

  return (
    <Box className='d-flex justify-content-center align-items-center' sx={{height: '100vh'}}>
      {isPrivate &&
        <Typography className='position-absolute' sx={{top: '1rem', left: '1rem'}} variant='caption'>
          Internal
        </Typography>
      }
      {regWelcome
        ? <Box className='d-flex flex-column align-items-center gap-4 p-5'>
          <Typography className="text-center" variant='h5'>We've sent you a verification email,<br/>Click the link to start using your account</Typography>
          <Button className='fs-5 px-3' variant="contained" href='../login'>Continue</Button>
        </Box>
        : <Box className='d-flex flex-column align-items-center gap-3 p-5 w-100' component="form" sx={{minWidth: 300, maxWidth: 450}}>
          <img src={logo} width={80} className='mb-4' alt="PATHOMIQ"/>
          {/* <Typography className='mb-3' variant="h4" component="h1">Sign Up</Typography> */}
          <TextField
            label="Title"
            name='title'
            variant="outlined"
            value={signupForm.title}
            onChange={handleChange}
            error={!!signupFormError.title}
            helperText={signupFormError.title}
            sx={{width: '100%'}}
            required
          />
          <TextField
            label="First Name"
            name='first_name'
            variant="outlined"
            value={signupForm.first_name}
            onChange={handleChange}
            error={!!signupFormError.first_name}
            helperText={signupFormError.first_name}
            sx={{width: '100%'}}
            required
          />
          <TextField
            label="Last Name"
            name='last_name'
            variant="outlined"
            value={signupForm.last_name}
            onChange={handleChange}
            error={!!signupFormError.last_name}
            helperText={signupFormError.last_name}
            sx={{width: '100%'}}
            required
          />
          <TextField
            label="Email"
            name='email'
            variant="outlined"
            value={signupForm.email}
            onChange={handleChange}
            error={!!signupFormError.email}
            helperText={signupFormError.email}
            sx={{width: '100%'}}
            autoComplete="browser-off"
            required
          />
          <FormControl sx={{width: '100%'}} variant="outlined" error={!!signupFormError.password} required>
            <InputLabel htmlFor="login-password">Password</InputLabel>
            <OutlinedInput id="login-password" type={showPassword ? 'text' : 'password'} 
              endAdornment={
                <InputAdornment position="end">
                  <IconButton aria-label={showPassword ? 'hide the password' : 'display the password'}
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    onMouseUp={handleMouseUpPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="Password"
              name='password'
              value={signupForm.password}
              onChange={handleChange}
              autoComplete="new-password"
            />
            <FormHelperText error={true}>{signupFormError.password}</FormHelperText>
          </FormControl>
          <FormControl sx={{width: '100%'}} variant="outlined" error={!!signupFormError.confirm_password} required>
            <InputLabel htmlFor="login-confrim-password">Confirm Password</InputLabel>
            <OutlinedInput id="login-confrim-password" type={showConfirmPassword ? 'text' : 'password'} 
              endAdornment={
                <InputAdornment position="end">
                  <IconButton aria-label={showConfirmPassword ? 'hide the password' : 'display the password'}
                    onClick={handleClickShowConfirmPassword}
                    onMouseDown={handleMouseDownConfirmPassword}
                    onMouseUp={handleMouseUpConfirmPassword}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="Confirm Password"
              name='confirm_password'
              value={signupForm.confirm_password}
              onChange={handleChange}
            />
            <FormHelperText error={true}>{signupFormError.confirm_password}</FormHelperText>
          </FormControl>
          <TextField
            label="Organization"
            name='organization'
            variant="outlined"
            value={signupForm.organization}
            onChange={handleChange}
            error={!!signupFormError.organization}
            helperText={signupFormError.organization}
            sx={{width: '100%'}}
            required
          />
          {formResMsg.length ? <Typography color='error'>{formResMsg}</Typography> : null}
          <Button className='p-2 w-100 fs-6' variant="contained" onClick={signupHandler} disabled={isLoading}>
            {isLoading ? <CircularProgress size={25}/> : 'Sign Up'}
          </Button>
          <Box className='d-flex justify-content-center'>
            <Typography>Already registered? <Link href="../login">Login</Link></Typography>
          </Box>
        </Box>
      }
    </Box>
  )
}

export default SignUp
